import { useState } from 'react';
import { api } from '../api';
import { asuntoLegible, enlacesVisiblesDe, esPromocional } from '../correo';
import { useT } from '../i18n';

/**
 * Un correo que llegó a una casilla del panel.
 *
 * El cuerpo es el mensaje CRUDO, con cabeceras y todo: el manejador lo guarda sin interpretar
 * porque analizar MIME arriesga el límite de CPU del Worker, y un fallo ahí pierde el correo.
 * Todo lo que hace falta para leerlo pasa en el navegador.
 */
export type CorreoView = {
  id: string;
  remitente: string;
  asunto: string;
  cuerpo: string;
  recibidoEn: string;
};

/**
 * La bandeja, en un solo lugar.
 *
 * ── Por qué es un componente y no dos pantallas parecidas ─────────────────────
 * Porque eran dos: la casilla de una influencer en su ficha, y la del manager en Credenciales,
 * con la misma marcación copiada. Copiada quiere decir que un arreglo hay que hacerlo dos veces
 * y que la segunda se olvida — que es lo que pasó con el asunto sin decodificar y con el volcado
 * de quince URLs por mensaje.
 */
export function ListaDeCorreos({
  correos,
  onChange,
}: {
  correos: CorreoView[];
  onChange: () => void;
}) {
  const { t } = useT();
  /*
    Lo que es trámite arriba, y la publicidad plegada abajo.

    Esta casilla existe para una sola cosa —el enlace de confirmación de un alta— y apenas la
    dirección queda registrada, la plataforma empieza a mandar campañas. Cuatro de Fanvue en dos
    días fue suficiente para tapar el correo que se estaba esperando.
  */
  const promocionales = correos.filter(esPromocional);
  const utiles = correos.filter((correo) => !esPromocional(correo));

  return (
    <div>
      {utiles.length === 0 && promocionales.length > 0 && (
        <p className="mb-3 text-sm text-[var(--color-ink-muted)]">
          {t('correo.soloPublicidad')}
        </p>
      )}

      <ul className="divide-y divide-[var(--color-border-subtle)]">
        {utiles.map((correo) => (
          <Correo key={correo.id} correo={correo} onChange={onChange} />
        ))}
      </ul>

      {promocionales.length > 0 && (
        <details className="mt-3 rounded-xl border border-[var(--color-border-subtle)]">
          <summary className="cursor-pointer list-none p-3 text-sm">
            <span className="font-medium">{t('correo.publicidad', { n: promocionales.length })}</span>
            <span className="ml-2 text-xs text-[var(--color-ink-muted)]">
              {t('correo.publicidadDetalle')}
            </span>
          </summary>
          {/*
            Plegada, no descartada.

            Reconocer publicidad es una heurística —mira `List-Unsubscribe` y el remitente— y
            puede errar. Un enlace de verificación que no aparece deja un alta trabada sin
            explicación, así que lo que se equivoque sigue estando a un clic.
          */}
          <ul className="divide-y divide-[var(--color-border-subtle)] px-3 pb-1">
            {promocionales.map((correo) => (
              <Correo key={correo.id} correo={correo} onChange={onChange} promocional />
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Correo({
  correo,
  onChange,
  promocional = false,
}: {
  correo: CorreoView;
  onChange: () => void;
  promocional?: boolean;
}) {
  const { t } = useT();
  const [abierto, setAbierto] = useState(false);

  /*
    En la publicidad no se muestra ningún enlace.

    Sus quince URLs de seguimiento son justamente lo que hacía ilegible la bandeja, y ninguna es
    una acción que alguien tenga que completar. El mensaje entero sigue a un clic.
  */
  const enlaces = promocional ? [] : enlacesVisiblesDe(correo.cuerpo);

  return (
    <li className="py-3">
      <p className="text-sm font-medium">{asuntoLegible(correo.asunto) || t('correo.sinAsunto')}</p>
      <p className="mt-0.5 truncate text-xs text-[var(--color-ink-muted)]">{correo.remitente}</p>

      {/*
        Los enlaces se ven sin pulsar nada.

        Es lo único que se viene a buscar acá —el de verificación o el de restablecimiento— y
        esconderlo detrás de un clic obliga a descubrir que el asunto era pulsable. Ya pasó:
        parecía texto.
      */}
      {enlaces.map((enlace) => (
        <a
          key={enlace}
          href={enlace}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-2 block truncate rounded-lg border border-[var(--color-border-subtle)] px-3 py-2 text-xs underline underline-offset-2 hover:border-[var(--color-accent)]"
          title={enlace}
        >
          {enlace}
        </a>
      ))}

      <div className="mt-2 flex flex-wrap gap-3 text-xs">
        <button
          type="button"
          onClick={() => setAbierto((valor) => !valor)}
          className="text-[var(--color-ink-muted)] underline underline-offset-2 hover:text-[var(--color-accent)]"
        >
          {t(abierto ? 'correo.ocultarMensaje' : 'correo.verMensaje')}
        </button>
        {/*
          Descartar, porque un enlace de verificación sirve una vez.

          Sin esto el mensaje cumplido se queda treinta días —hasta que la retención lo borre—
          justo donde hay que ver el que llegó recién.
        */}
        <button
          type="button"
          onClick={() => void api.del(`/api/correo/${correo.id}`).then(onChange)}
          className="text-[var(--color-ink-muted)] underline underline-offset-2 hover:text-[var(--color-danger)]"
        >
          {t('correo.descartar')}
        </button>
      </div>

      {abierto && (
        <pre className="mt-2 max-h-80 overflow-auto rounded-lg bg-[var(--color-surface-sunken)] p-3 text-xs break-all whitespace-pre-wrap">
          {correo.cuerpo}
        </pre>
      )}
    </li>
  );
}
